import { ImmutableUtils } from '@atlassian/mpac-utils';
import { List, Map, Set } from 'immutable';
import flow from 'lodash/flow';
import algoliaFacets from "../../common/config/algolia-facets";
import { getFilters } from "../../common/config/filters";
import { sortOptions } from "../../common/config/sorting";
import { PVA } from "../../common/constants/paymentTypes";
import { BAMBOO, BITBUCKET, CONFLUENCE, CROWD, FECRU, HIPCHAT, JIRA, JIRA_SERVICE_DESK, TRELLO } from "../../common/constants/products";
import SearchParamsUpdate from "../../common/records/SearchParamsUpdate";
import { searchParamsUpdateForAddonKeys } from "../../common/utils/algolia";
export const HOME = 'home';
export const CollectionsEnum = {
  FEATURED: 'featured',
  TOP_SELLING: 'topSelling',
  TOP_SELLING_CLOUD: 'topSellingCloud',
  TOP_SELLING_SERVER: 'topSellingServer',
  RECENTLY_ADDED: 'recentlyAdded',
  TRENDING: 'trending',
  NATURAL_WITHOUT_FEATURED: 'naturalWithoutFeatured',
  CLOUD_FORTIFIED: 'cloudFortified'
};
function genericFeaturedCollection(title, tagline) {
  return featuredCollectionFactory({
    baseQueries: List([jiraBaseQuery, confluenceBaseQuery, bitbucketBaseQuery, hipchatBaseQuery]),
    numDisplayResults: featuredCollectionNumDisplayResults,
    resolveHits: _ref => {
      let {
        hits
      } = _ref;
      let finalHits = List();
      for (let i = 0; i < featuredCollectionNumDisplayResults; ++i) {
        const indexToRetrieveFrom = i % hits.size;
        const hs = hits.get(indexToRetrieveFrom);
        finalHits = finalHits.push(ImmutableUtils.sample(hs.filter(h => !finalHits.includes(h)), 1).get(0));
      }
      return finalHits.filter(h => h);
    },
    title,
    tagline
  });
}
function curatedFeaturedCollection(curatedApps, title, tagline) {
  return featuredCollectionFactory({
    baseQueries: List([emptyQuery.update('applyOverrides', applyOverrides => applyOverrides.compose(searchParamsUpdateForAddonKeys(curatedApps)))]),
    numDisplayResults: featuredCollectionNumDisplayResults,
    resolveHits: flow([singleResolveHits, preserveHitsOrder(curatedApps)]),
    title,
    tagline
  });
}
const emptyQuery = Map({
  filters: Map(),
  applyOverrides: SearchParamsUpdate.default_()
});
const baseWithoutAtlassianQuery = emptyQuery.update('applyOverrides', applyOverrides => applyOverrides.compose({
  types: Set([algoliaFacets.get('isAtlassian')]),
  apply: params => params.addFacetRefinement(algoliaFacets.get('isAtlassian').algoliaFacetKey, 'false')
}));
const basePaidViaAtlassianQuery = baseWithoutAtlassianQuery.update('applyOverrides', applyOverrides => applyOverrides.composeApply(p => p.addDisjunctiveFacetRefinement(algoliaFacets.get('marketplaceType').algoliaFacetKey, PVA)));
export const LARGE_COLLECTION_TYPE = 'LARGE_COLLECTION_TYPE';
export const SMALL_COLLECTION_TYPE = 'SMALL_COLLECTION_TYPE';
const jiraBaseQuery = basePaidViaAtlassianQuery.update('filters', filters => filters.set('product', JIRA));
const confluenceBaseQuery = basePaidViaAtlassianQuery.update('filters', filters => filters.set('product', CONFLUENCE));
const bitbucketBaseQuery = basePaidViaAtlassianQuery.update('filters', filters => filters.set('product', BITBUCKET));
const hipchatBaseQuery = baseWithoutAtlassianQuery.update('filters', filters => filters.set('product', HIPCHAT).set('marketingLabel', Set(['hipchat-default'])));
const resolveProductLabel = _ref2 => {
  let {
    productKey,
    options
  } = _ref2;
  return getFilters({
    featureFlags: options
  }).getIn(['product', 'customFilters', productKey, 'label']);
};
const resolveBaseWithoutAtlassianQueries = _ref3 => {
  let {
    baseFilters,
    baseApplyOverrides
  } = _ref3;
  return resolveQueriesFactory({
    baseQuery: baseWithoutAtlassianQuery,
    baseFilters,
    baseApplyOverrides
  });
};
const resolveQueriesFactory = _ref4 => {
  let {
    baseQuery,
    baseFilters,
    baseApplyOverrides
  } = _ref4;
  baseFilters = baseFilters || Map();
  baseApplyOverrides = baseApplyOverrides || SearchParamsUpdate.default_();
  return function () {
    let {
      filters,
      applyOverrides
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      filters: Map(),
      applyOverrides: SearchParamsUpdate.default_()
    };
    const apo = applyOverrides || SearchParamsUpdate.default_();
    return List([baseQuery.update('filters', fs => fs.merge(baseFilters).merge(filters)).update('applyOverrides', ao => ao.compose(baseApplyOverrides).compose(apo))]);
  };
};
const singleResolveHits = _ref5 => {
  let {
    hits
  } = _ref5;
  return ImmutableUtils.sample(hits.flatten(1), 6);
};
const preserveHitsOrder = addonKeys => hits => hits.sort((a, b) => addonKeys.indexOf(a.get('addonKey')) - addonKeys.indexOf(b.get('addonKey')));
const flattenHits = _ref6 => {
  let {
    hits
  } = _ref6;
  return hits.flatten(1);
};
const featuredCollectionFactory = _ref7 => {
  let {
    baseQueries,
    numDisplayResults,
    resolveHits,
    title,
    tagline
  } = _ref7;
  return Map({
    key: CollectionsEnum.FEATURED,
    analyticsTileType: 'exploreTile',
    numResults: 10,
    numDisplayResults,
    title,
    tagline,
    type: LARGE_COLLECTION_TYPE,
    hideViewMore: true,
    recordTileImpressions: true,
    usesContentOverride: true,
    resolveQueries: _ref8 => {
      let {
        filters,
        applyOverrides = SearchParamsUpdate.default_()
      } = _ref8;
      return baseQueries.map(bq => bq.update('filters', fs => fs.mergeDeep(filters)).update('applyOverrides', ao => ao.compose(applyOverrides)));
    },
    resolveHits
  });
};
const topSellingCollectionFactory = function topSellingCollectionFactory() {
  let {
    productKey,
    tagline,
    options
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Map({
    key: CollectionsEnum.TOP_SELLING,
    analyticsTileType: 'topSelling',
    numResults: 9,
    title: productKey ? "Top-selling ".concat(resolveProductLabel({
      productKey,
      options
    }), " apps") : 'Top-selling apps',
    tagline,
    type: SMALL_COLLECTION_TYPE,
    resolveQueries: resolveBaseWithoutAtlassianQueries({
      baseFilters: topSellingBaseFilters
    }),
    resolveHits: flattenHits,
    filters: topSellingBaseFilters
  });
};
const topSellingBaseFilters = Map({
  sort: sortOptions.get('topSelling').get('key')
});
const topSellingServerCollectionFactory = function topSellingServerCollectionFactory() {
  let {
    productKey,
    tagline,
    options
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Map({
    key: 'topSellingServer',
    analyticsTileType: 'topSellingServer',
    numResults: 8,
    title: productKey ? "Top-selling ".concat(resolveProductLabel({
      productKey,
      options
    }), " Server apps") : 'Top-selling Server apps',
    tagline,
    type: SMALL_COLLECTION_TYPE,
    resolveQueries: resolveBaseWithoutAtlassianQueries({
      baseFilters: topSellingServerBaseFilters
    }),
    resolveHits: flattenHits,
    filters: topSellingServerBaseFilters
  });
};
const topSellingServerBaseFilters = Map({
  sort: sortOptions.get('topSelling').get('key'),
  hosting: 'server'
});
const topSellingCloudCollectionFactory = function topSellingCloudCollectionFactory() {
  let {
    productKey,
    tagline,
    options
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Map({
    key: 'topSellingCloud',
    analyticsTileType: 'topSellingCloud',
    numResults: 9,
    title: productKey ? "Top-selling ".concat(resolveProductLabel({
      productKey,
      options
    }), " Cloud apps") : 'Top-selling Cloud apps',
    tagline,
    type: SMALL_COLLECTION_TYPE,
    resolveQueries: resolveBaseWithoutAtlassianQueries({
      baseFilters: topSellingCloudBaseFilters
    }),
    resolveHits: flattenHits,
    filters: topSellingCloudBaseFilters
  });
};
const topSellingCloudBaseFilters = Map({
  sort: sortOptions.get('topSelling').get('key'),
  hosting: 'cloud'
});
const recentlyAddedCollectionFactory = function recentlyAddedCollectionFactory() {
  let {
    productKey,
    tagline,
    options
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Map({
    key: CollectionsEnum.RECENTLY_ADDED,
    analyticsTileType: 'newestTile',
    numResults: 9,
    title: productKey ? "Newest ".concat(resolveProductLabel({
      productKey,
      options
    }), " apps") : 'Newest apps',
    tagline,
    type: SMALL_COLLECTION_TYPE,
    resolveQueries: resolveBaseWithoutAtlassianQueries({
      baseFilters: recentlyAddedBaseFilters
    }),
    resolveHits: flattenHits,
    filters: recentlyAddedBaseFilters
  });
};
const recentlyAddedBaseFilters = Map({
  sort: sortOptions.get('newest').get('key')
});
const trendingCollectionFactory = function trendingCollectionFactory() {
  let {
    productKey,
    tagline,
    options
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Map({
    key: CollectionsEnum.TRENDING,
    analyticsTileType: 'trendingTile',
    numResults: 9,
    title: productKey ? "Trending ".concat(resolveProductLabel({
      productKey,
      options
    }), " apps") : 'Trending apps',
    tagline,
    type: SMALL_COLLECTION_TYPE,
    resolveQueries: resolveBaseWithoutAtlassianQueries({
      baseFilters: trendingBaseFilters
    }),
    resolveHits: flattenHits,
    filters: trendingBaseFilters
  });
};
const trendingBaseFilters = Map({
  sort: sortOptions.get('trending').get('key')
});
const featuredCollectionNumDisplayResults = 6;
export const cloudFortifiedFilter = Map({
  moreFilters: Set(['cloudFortified'])
});
const cloudFortifiedAppsCollectionFactory = () => Map({
  key: CollectionsEnum.CLOUD_FORTIFIED,
  analyticsTileType: 'cloudFortifiedAppTile',
  numResults: 3,
  title: 'Cloud Fortified Apps',
  tagline: "Cloud apps with additional security, reliability, and support to meet your organization's needs.",
  type: LARGE_COLLECTION_TYPE,
  resolveHits: flattenHits,
  hideViewMore: false,
  resolveQueries: resolveQueriesFactory({
    baseQuery: cloudFortifiedAppQuery
  }),
  filters: cloudFortifiedFilter
});
const cloudFortifiedAppQuery = basePaidViaAtlassianQuery.update('applyOverrides', applyOverrides => applyOverrides.composeApply(p => p.addFacetRefinement(algoliaFacets.get('cloudFortified').algoliaFacetKey, 'approved')));
const getHomeCollections = (featuredAddons, options) => {
  const title = featuredAddons.title;
  const tagline = featuredAddons.tagline;
  const showCloudFortifiedAppsCollection = options === null || options === void 0 ? void 0 : options.enableCloudFortifiedAppsDiscovery;
  const featuredCollection = featuredAddons.curatedApps ? curatedFeaturedCollection(featuredAddons.curatedApps, title, tagline) : genericFeaturedCollection(title, tagline);
  return List([featuredCollection, ...(showCloudFortifiedAppsCollection ? [cloudFortifiedAppsCollectionFactory()] : []), topSellingCollectionFactory({
    options
  }), recentlyAddedCollectionFactory({
    options
  }), trendingCollectionFactory({
    options
  })]);
};
const resolveHits = (product, featuredAddonsConfig) => {
  const featuredAddons = featuredAddonsConfig.get(product);
  return featuredAddons !== null && featuredAddons !== void 0 && featuredAddons.curatedApps ? flow([singleResolveHits, preserveHitsOrder(featuredAddons.curatedApps)]) : singleResolveHits;
};
const resolveProductQueries = _ref9 => {
  let {
    productBaseQuery,
    product,
    featuredAddonsConfig
  } = _ref9;
  const featuredAddons = featuredAddonsConfig.get(product);
  return List([featuredAddons !== null && featuredAddons !== void 0 && featuredAddons.curatedApps ? emptyQuery.update('applyOverrides', applyOverrides => applyOverrides.compose(searchParamsUpdateForAddonKeys(featuredAddons.curatedApps))) : productBaseQuery.update('filters', filters => filters.set('product', product))]);
};

// eslint-disable-next-line max-lines-per-function -- Fix me
export default (_ref10 => {
  let {
    featuredAddonsConfig,
    options
  } = _ref10;
  return Map({
    [HOME]: getHomeCollections(featuredAddonsConfig.get(HOME), options),
    [JIRA]: List([featuredCollectionFactory({
      baseQueries: resolveProductQueries({
        productBaseQuery: basePaidViaAtlassianQuery,
        product: JIRA,
        featuredAddonsConfig
      }),
      numDisplayResults: featuredCollectionNumDisplayResults,
      resolveHits: resolveHits(JIRA, featuredAddonsConfig),
      title: featuredAddonsConfig.get(JIRA).title,
      tagline: featuredAddonsConfig.get(JIRA).tagline
    }), topSellingCloudCollectionFactory({
      productKey: JIRA,
      options,
      tagline: 'Your team likes to move fast. From continuous integration to DevOps planning, our apps helps maintain velocity on demand.'
    }), topSellingServerCollectionFactory({
      productKey: JIRA,
      options,
      tagline: 'Apps for Jira Server allows your team to scale to address the needs across your organization.'
    }), recentlyAddedCollectionFactory({
      productKey: JIRA,
      options
    }), trendingCollectionFactory({
      productKey: JIRA,
      options
    })]),
    [JIRA_SERVICE_DESK]: List([featuredCollectionFactory({
      baseQueries: resolveProductQueries({
        productBaseQuery: basePaidViaAtlassianQuery,
        product: JIRA_SERVICE_DESK,
        featuredAddonsConfig
      }),
      numDisplayResults: featuredCollectionNumDisplayResults,
      resolveHits: resolveHits(JIRA_SERVICE_DESK, featuredAddonsConfig),
      title: featuredAddonsConfig.get(JIRA_SERVICE_DESK).title,
      tagline: featuredAddonsConfig.get(JIRA_SERVICE_DESK).tagline
    }), recentlyAddedCollectionFactory({
      productKey: JIRA_SERVICE_DESK,
      options
    }), trendingCollectionFactory({
      productKey: JIRA_SERVICE_DESK,
      options
    }), topSellingCloudCollectionFactory({
      productKey: JIRA_SERVICE_DESK,
      options
    }), topSellingServerCollectionFactory({
      productKey: JIRA_SERVICE_DESK,
      options
    })]),
    [CONFLUENCE]: List([featuredCollectionFactory({
      baseQueries: resolveProductQueries({
        productBaseQuery: basePaidViaAtlassianQuery,
        product: CONFLUENCE,
        featuredAddonsConfig
      }),
      numDisplayResults: featuredCollectionNumDisplayResults,
      resolveHits: resolveHits(CONFLUENCE, featuredAddonsConfig),
      title: featuredAddonsConfig.get(CONFLUENCE).title,
      tagline: featuredAddonsConfig.get(CONFLUENCE).tagline
    }), recentlyAddedCollectionFactory({
      productKey: CONFLUENCE,
      options
    }), trendingCollectionFactory({
      productKey: CONFLUENCE,
      options
    }), topSellingCloudCollectionFactory({
      productKey: CONFLUENCE,
      options
    }), topSellingServerCollectionFactory({
      productKey: CONFLUENCE,
      options
    })]),
    [BITBUCKET]: List([featuredCollectionFactory({
      baseQueries: resolveProductQueries({
        productBaseQuery: basePaidViaAtlassianQuery,
        product: BITBUCKET,
        featuredAddonsConfig
      }),
      title: featuredAddonsConfig.get(BITBUCKET).title,
      tagline: featuredAddonsConfig.get(BITBUCKET).tagline,
      numDisplayResults: featuredCollectionNumDisplayResults,
      resolveHits: resolveHits(BITBUCKET, featuredAddonsConfig)
    }), topSellingCollectionFactory({
      productKey: BITBUCKET,
      options
    }), recentlyAddedCollectionFactory({
      productKey: BITBUCKET,
      options
    }), trendingCollectionFactory({
      productKey: BITBUCKET,
      options
    })]),
    [TRELLO]: List([featuredCollectionFactory({
      baseQueries: resolveProductQueries({
        productBaseQuery: basePaidViaAtlassianQuery,
        product: TRELLO,
        featuredAddonsConfig
      }),
      numDisplayResults: featuredCollectionNumDisplayResults,
      resolveHits: resolveHits(TRELLO, featuredAddonsConfig)
    }), topSellingCollectionFactory({
      productKey: TRELLO,
      options
    }), recentlyAddedCollectionFactory({
      productKey: TRELLO,
      options
    }), trendingCollectionFactory({
      productKey: TRELLO,
      options
    })]),
    [HIPCHAT]: List([featuredCollectionFactory({
      baseQueries: resolveProductQueries({
        productBaseQuery: baseWithoutAtlassianQuery.update('filters', filters => filters.set('marketingLabel', Set(['hipchat-default']))),
        product: HIPCHAT,
        featuredAddonsConfig
      }),
      numDisplayResults: featuredCollectionNumDisplayResults,
      resolveHits: resolveHits(HIPCHAT, featuredAddonsConfig)
    }), recentlyAddedCollectionFactory({
      productKey: HIPCHAT,
      options
    }), trendingCollectionFactory({
      productKey: HIPCHAT,
      options
    })]),
    [FECRU]: List([featuredCollectionFactory({
      baseQueries: resolveProductQueries({
        productBaseQuery: basePaidViaAtlassianQuery,
        product: FECRU,
        featuredAddonsConfig
      }),
      numDisplayResults: featuredCollectionNumDisplayResults,
      resolveHits: resolveHits(FECRU, featuredAddonsConfig)
    }), topSellingCollectionFactory({
      productKey: FECRU,
      options
    }), recentlyAddedCollectionFactory({
      productKey: FECRU,
      options
    }), trendingCollectionFactory({
      productKey: FECRU,
      options
    })]),
    [BAMBOO]: List([featuredCollectionFactory({
      baseQueries: resolveProductQueries({
        productBaseQuery: basePaidViaAtlassianQuery,
        product: BAMBOO,
        featuredAddonsConfig
      }),
      numDisplayResults: featuredCollectionNumDisplayResults,
      resolveHits: resolveHits(BAMBOO, featuredAddonsConfig)
    }), topSellingCollectionFactory({
      productKey: BAMBOO,
      options
    }), recentlyAddedCollectionFactory({
      productKey: BAMBOO,
      options
    }), trendingCollectionFactory({
      productKey: BAMBOO,
      options
    })]),
    [CROWD]: List([featuredCollectionFactory({
      baseQueries: resolveProductQueries({
        productBaseQuery: baseWithoutAtlassianQuery,
        product: CROWD,
        featuredAddonsConfig
      }),
      numDisplayResults: featuredCollectionNumDisplayResults,
      resolveHits: resolveHits(CROWD, featuredAddonsConfig)
    }), recentlyAddedCollectionFactory({
      productKey: CROWD,
      options
    }), trendingCollectionFactory({
      productKey: CROWD,
      options
    })])
  });
});